import axios from 'axios';
import { API_URL } from '../config';

// Query Keys
export const queryKeys = {
  user: {
    all: ['user'] as const,
    profile: (username: string) => [...queryKeys.user.all, 'profile', username] as const,
    me: () => [...queryKeys.user.all, 'me'] as const,
    friends: () => [...queryKeys.user.all, 'friends'] as const,
  },
  hikes: {
    all: ['hikes'] as const,
    list: (userId: string) => [...queryKeys.hikes.all, 'list', userId] as const,
    details: (hikeId: string) => [...queryKeys.hikes.all, 'details', hikeId] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    unread: () => [...queryKeys.notifications.all, 'unread'] as const,
  },
  achievements: {
    all: ['achievements'] as const,
    user: (userId: string) => [...queryKeys.achievements.all, 'user', userId] as const,
  },
};

// Types
export interface User {
  _id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  bio?: string;
  stats: {
    totalHikes: number;
    totalDistance: number;
    totalElevation: number;
    averageRating: number;
  };
  friends: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Hike {
  _id: string;
  name: string;
  country: string;
  difficulty: 'Easy' | 'Moderate' | 'Hard' | 'Very Hard';
  length: number;
  elevation: number;
  rating: number;
  rank: number;
  link?: string;
  userId: string;
  likes: string[];
  isLiked: boolean;
  completionCount?: number;
  notes?: string;
  completedOn?: string;
  weatherConditions?: string[];
  photo?: {
    url: string;
    uploadedAt: string;
  };
  gpxFile?: {
    s3Key: string;
    fileName: string;
    fileSize: number;
    uploadDate: string;
    isShared?: boolean;
  };
  isWishList: boolean;
  user: string;
}

export interface Notification {
  _id: string;
  userId: string;
  type: 'hike_logged' | 'badge_achieved' | 'milestone_reached';
  content: string;
  relatedEntityId: string;
  read: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Achievement {
  _id: string;
  name: string;
  description: string;
  icon: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
}

// API Client
const api = axios.create({
  baseURL: API_URL,
  withCredentials: false, // Don't use cookies, use Authorization headers
});

// Add request interceptor to include Authorization header
api.interceptors.request.use(
  (config) => {
    // Add Authorization header from localStorage
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const { authService } = await import('../services/authService');
        const refreshSuccess = await authService.refreshToken();
        if (refreshSuccess) {
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Error during token refresh:', refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// API Functions
export const apiClient = {
  // Auth
  login: async (loginId: string, password: string): Promise<{ user: User }> => {
    const { data } = await api.post('/auth/login', { loginId, password });
    return data;
  },

  register: async (userData: Omit<User, "_id"> & { password: string }): Promise<{ user: User }> => {
    const { data } = await api.post('/auth/register', userData);
    return data;
  },

  // User
  getUser: async (username: string): Promise<User> => {
    const { data } = await api.get(`/users/${username}`);
    return data;
  },

  getCurrentUser: async (): Promise<User> => {
    const { data } = await api.get('/users/me');
    return data;
  },

  updateUser: async (updates: Partial<User>): Promise<User> => {
    const { data } = await api.patch('/users/me', updates);
    return data;
  },

  addFriend: async (userId: string): Promise<void> => {
    await api.post(`/users/friends/${userId}`);
  },

  removeFriend: async (userId: string): Promise<void> => {
    await api.delete(`/users/friends/${userId}`);
  },

  // Hikes
  getHikes: async (userId: string): Promise<Hike[]> => {
    const { data } = await api.get(`/hikes?userId=${userId}`);
    return data;
  },

  getMyHikes: async (): Promise<Hike[]> => {
    const { data } = await api.get('/hikes/my-hikes');
    return data;
  },

  getUserHikes: async (userId: string): Promise<Hike[]> => {
    const { data } = await api.get(`/hikes/user/${userId}`);
    return data;
  },

  getWishlist: async (userId: string): Promise<Hike[]> => {
    const { data } = await api.get(`/hikes/user/${userId}/wishlist`);
    return data;
  },

  getMyWishlist: async (): Promise<Hike[]> => {
    const { data } = await api.get('/hikes/my-wishlist');
    return data;
  },

  getRankedHikes: async (userId: string): Promise<Hike[]> => {
    const { data } = await api.get(`/hikes/user/${userId}/ranked`);
    return data;
  },

  createHike: async (hikeData: any): Promise<Hike> => {
    const { data } = await api.post('/hikes', hikeData);
    return data;
  },

  updateHike: async (hikeId: string, updates: any): Promise<Hike> => {
    const { data } = await api.patch(`/hikes/${hikeId}`, updates);
    return data;
  },

  deleteHike: async (hikeId: string): Promise<{ stats: User['stats'] }> => {
    const { data } = await api.delete(`/hikes/${hikeId}`);
    return data;
  },

  reorderHikes: async (hikes: { _id: string; rank: number }[]): Promise<{ stats: User['stats'] }> => {
    const { data } = await api.put('/hikes/reorder', { hikes });
    return data;
  },

  likeHike: async (hikeId: string): Promise<void> => {
    await api.post(`/hikes/${hikeId}/like`);
  },

  unlikeHike: async (hikeId: string): Promise<void> => {
    await api.delete(`/hikes/${hikeId}/like`);
  },

  uploadHikePhoto: async (hikeId: string, formData: FormData): Promise<any> => {
    const { data } = await api.post(`/hikes/${hikeId}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  uploadHikeGPX: async (hikeId: string, formData: FormData): Promise<any> => {
    const { data } = await api.post(`/hikes/${hikeId}/gpx`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  getHikeGPX: async (hikeId: string): Promise<any> => {
    const { data } = await api.get(`/hikes/${hikeId}/gpx`);
    return data;
  },

  getHikePhoto: async (hikeId: string): Promise<any> => {
    const { data } = await api.get(`/hikes/${hikeId}/photo`);
    return data;
  },

  // Friends
  getFriends: async (): Promise<User[]> => {
    const { data } = await api.get('/users/friends');
    return data;
  },

  getFriendsHikes: async (): Promise<Hike[]> => {
    const { data } = await api.get('/hikes/friends');
    return data;
  },

  // Notifications
  getNotifications: async (): Promise<{ notifications: Notification[]; unreadCount: number }> => {
    const { data } = await api.get('/notifications');
    // The backend already returns the correct format
    return data;
  },

  markNotificationsAsRead: async (): Promise<void> => {
    await api.put('/notifications/read');
  },

  deleteNotification: async (notificationId: string): Promise<void> => {
    await api.delete(`/notifications/${notificationId}`);
  },

  // General
  getTopHikes: async (): Promise<Hike[]> => {
    const { data } = await api.get('/hikes/top');
    return data;
  },

  getRandomHikesWithPhotos: async (): Promise<Hike[]> => {
    const { data } = await api.get('/hikes/random-with-photo');
    return data;
  },

  getCountries: async (): Promise<string[]> => {
    const { data } = await api.get('/hikes/countries');
    return data;
  },

  searchHikes: async (query: string): Promise<Hike[]> => {
    const { data } = await api.get(`/hikes/search?query=${encodeURIComponent(query)}`);
    return data;
  },

  getHikeTemplates: async (query: string): Promise<Hike[]> => {
    const { data } = await api.get(`/hikes/templates?query=${encodeURIComponent(query)}`);
    return data;
  },
}; 